import { Injectable, Injector } from '@angular/core';
import { ToastrService, IndividualConfig } from 'ngx-toastr';
import { AppNotification } from '@deltager/organizers/admin/shared/models/AppNotification';
import { AppNotificationType } from '@deltager/organizers/admin/shared/models/AppNotificationType';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class AppNotificationsService {
  private config: Partial<IndividualConfig>;

  constructor(private injector: Injector) {
    this.config = {
      positionClass: 'toast-bottom-right',
      tapToDismiss: false,
    };
  }

  private get translations(): TranslateService {
    return this.injector.get(TranslateService);
  }

  private get toastr(): ToastrService {
    return this.injector.get(ToastrService);
  }

  public sendNotification(notification: AppNotification) {
    this.adjustNotification(notification);

    switch (notification.type) {
      case AppNotificationType.SUCCESS:
        this.sendSuccess(notification);
        break;
      case AppNotificationType.ERROR:
        this.sendError(notification);
        break;
      case AppNotificationType.INFO:
        this.sendInfo(notification);
        break;
      case AppNotificationType.WARNING:
        this.sendWarning(notification);
        break;
    }
  }

  public sendSuccessOnUpdate(entityName: string, isEditOperation: boolean = true) {
    let title: string;

    if (isEditOperation) {
      title = (this.translations.instant('feedback.x_was_saved') || '$1 ble lagret') as string;
    } else {
      title = (this.translations.instant('feedback.x_was_created') || 'Ny $1 ble opprettet') as string;
    }

    this.sendSuccess({ title: title.replace('$1', entityName) });
  }

  // Errors
  public sendErrorGeneric() {
    this.sendError({
      message: this.translations.instant('feedback.error_generic_message') || 'Prøv igjen eller vent til senere',
      title: this.translations.instant('validation.error_occured') || 'Det oppsto en feil',
    } as AppNotification);
  }

  public sendMissingFields() {
    this.sendNotification({
      type: AppNotificationType.WARNING,
      message:
        this.translations.instant('validation.required.fields') ||
        'Vennligst fyll ut alle påkrevde felter før du lagrer',
      title: this.translations.instant('validation.missing_fields') || 'Mangler informasjon',
    } as AppNotification);
  }

  public sendInfo(notification: AppNotification) {
    this.adjustNotification(notification);

    const config = {
      ...this.config,
      toastClass: 'ngx-custom-toastr',
      messageClass: 'ngx-custom-toastr-info-text',
      titleClass: 'ngx-custom-toastr-info-text',
    };
    this.toastr.info(notification.message, notification.title, config);
  }

  public sendWarning(notification: AppNotification) {
    this.adjustNotification(notification);

    const config = {
      ...this.config,
      toastClass: 'ngx-custom-toastr',
      messageClass: 'ngx-custom-toastr-warning-text',
      titleClass: 'ngx-custom-toastr-warning-text',
    };
    this.toastr.warning(notification.message, notification.title, config);
  }

  public sendError(notification: AppNotification) {
    this.adjustNotification(notification);

    const config = {
      ...this.config,
      toastClass: 'ngx-custom-toastr',
      messageClass: 'ngx-custom-toastr-error-text',
      titleClass: 'ngx-custom-toastr-error-text',
    };
    this.toastr.error(notification.message, notification.title, config);
  }

  public sendSuccess(notification: AppNotification) {
    this.adjustNotification(notification);

    const config = {
      ...this.config,
      toastClass: 'ngx-custom-toastr',
      messageClass: 'ngx-custom-toastr-success-text',
      titleClass: 'ngx-custom-toastr-success-text',
    };
    this.toastr.success(notification.message, notification.title, config);
  }

  private adjustNotification(notification: AppNotification): void {
    if (!notification) {
      return;
    }

    if (notification.message === notification.title) {
      notification.message = null;
    }
  }
}
